import * as React from 'react';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Control, Controller, ControllerRenderProps } from 'react-hook-form';
import { Box, FormHelperText, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { Grid } from "@mui/material";

type optionProps = {
  value: any,
  label: string,
}

interface CheckboxGroupProps {
  options: { value: string; label: string; description?: string; checked?: boolean; disabled?: boolean }[];
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  controllerName: string;
  control: Control<any, any>;
  rules?: object;
  xs? : number;
  isDisabled?: boolean;
  checkboxSx?: SxProps<Theme>;
  radioLabelSx?: SxProps<Theme>;
  cardMode?: boolean;
}

const CustomCheckboxGroup: React.FC<CheckboxGroupProps> = ({ options, onChange, controllerName, control, rules, xs, isDisabled, checkboxSx, radioLabelSx, cardMode }) => {

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>, field: ControllerRenderProps<any, string>, option: optionProps) =>{
    const checked = event.target.checked;
    const valueToAdd = option.value;
    let updatedValue;
    if (checked) {
      updatedValue = Array.isArray(field.value) ? [...field.value, valueToAdd] : [valueToAdd];
    } else {
      updatedValue = Array.isArray(field.value) ? field.value.filter((val) => val !== valueToAdd) : [];
    }
    field.onChange(updatedValue);
    onChange && onChange(event)
  }

  return (
    <Controller
      name={controllerName}
      control ={control}
      rules={rules? rules: {}}
      render={({ field, fieldState}) => (
        <FormGroup>
          {cardMode ? (
            /* ── Card mode — compact 2-col grid ── */
            <Grid container spacing={1}>
              {options.map((option) => {
                const isChecked = Array.isArray(field.value) && field.value.includes(option.value);
                return (
                  <Grid item xs={12} sm={6} key={option.value}>
                    <Box
                      onClick={() => {
                        if (isDisabled) return;
                        const syntheticEvent = {
                          target: { checked: !isChecked, value: option.value },
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleValueChange(syntheticEvent, field, option);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        cursor: isDisabled ? "default" : "pointer",
                        userSelect: "none",
                        border: `1.5px solid ${
                          isChecked
                            ? "rgba(20,184,166,0.5)"
                            : isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)"
                        }`,
                        backgroundColor: isChecked
                          ? isDark
                            ? "rgba(20,184,166,0.06)"
                            : "rgba(20,184,166,0.04)"
                          : isDark
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.015)",
                        opacity: isDisabled ? 0.5 : 1,
                        transition: "all 0.15s ease",
                        "&:hover": isDisabled
                          ? {}
                          : {
                              borderColor: isChecked
                                ? "#14b8a6"
                                : isDark
                                  ? "rgba(255,255,255,0.16)"
                                  : "rgba(0,0,0,0.16)",
                            },
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        disableRipple
                        size="small"
                        sx={{
                          color: isDark ? "#475569" : "#94a3b8",
                          "&.Mui-checked": { color: "#14b8a6" },
                          p: 0,
                          ...checkboxSx,
                        }}
                        tabIndex={-1}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            lineHeight: 1.3,
                          }}
                        >
                          {option.label}
                        </Typography>
                        {option.description && (
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: isDark ? "#64748b" : "#94a3b8",
                              lineHeight: 1.25,
                              mt: 0.1,
                            }}
                          >
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            /* ── Default mode (unchanged) ── */
            <Grid container spacing={1}>
              {options.map((option) => (
                <Grid item xs={xs} key={option.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color={theme.palette.mode === "dark" ? "secondary" : "primary"}
                        checked={Array.isArray(field.value) && field.value.includes(option.value)}
                        onChange={(e) => handleValueChange(e, field, option)}
                        sx={{
                          color: fieldState.error && !field.value
                            ? theme.palette.error.main
                            : '',
                          '&.Mui-checked': {
                            color: fieldState.error && !field.value
                              ? theme.palette.error.dark
                              : '',
                          },
                          ...checkboxSx,
                        }}
                        disabled={isDisabled}
                      />
                    }
                    label={option.label}
                    sx={{ color: fieldState.error ? theme.palette.error.main : 'inherit', ...radioLabelSx }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {/* CS-1403 - Moved the error out of the form control label to avoid misalignment of the error msg */}
          {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>} 
        </FormGroup>
      )}
    />
  );
};

export default CustomCheckboxGroup;